/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Modal, message, Input } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import { reportingApi } from '@/services/reporting/reporting-api';
import { RequestDTO } from '@/types/requestDTO';
import { useAuthStore } from '@/store/useAuthStore';
import CommonJs from '@/utils/commonJs';
import { RoleApproveHistoryView } from '@/types/reporting/RoleApproveHistoryView';
import { useLocalFilter } from '@/hooks/useOrganizationIndexedDBFilter';
import { useTranslation } from 'react-i18next';

interface ModalPheDuyetBaoCaoReportApproveCellProps {
  record: any;
  userID: string;
}

const Label: React.FC<{
  color: 'success' | 'danger' | 'primary' | 'warning' | 'info';
  title: React.ReactNode;
  onClick?: () => void;
}> = ({ color, title, onClick }) => {
  const isIconOnly =
    React.isValidElement(title) &&
    (title.type === HistoryOutlined ||
      (title.type as any)?.displayName === 'HistoryOutlined' ||
      (title.type as any)?.name === 'HistoryOutlined');
  return (
    <span
      style={{
        cursor: onClick ? 'pointer' : 'default',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isIconOnly ? '4px' : '2px 6px',
        marginRight: 6,
        borderRadius: isIconOnly ? '50%' : 3,
        fontSize: 12,
        fontWeight: 600,
        minWidth: isIconOnly ? '24px' : 'auto',
        height: '24px',
        lineHeight: '24px',
        verticalAlign: 'middle',
        color: color === 'warning' || color === 'info' || color === 'primary' ? '#0b446f' : '#fff',
        backgroundColor:
          color === 'success'
            ? '#28a745'
            : color === 'danger'
              ? '#dc3545'
              : color === 'warning'
                ? '#ffc107'
                : color === 'primary'
                  ? '#0d6efd'
                  : '#17a2b8',
      }}
      onClick={onClick}
    >
      {title}
    </span>
  );
};

export const ModalPheDuyetBaoCaoReportApproveCell: React.FC<ModalPheDuyetBaoCaoReportApproveCellProps> = ({ record, userID }) => {
  const { t } = useTranslation();
  const { loadEmployee, loadDepartment, loadPosition } = useLocalFilter();
  const item = record.__raw as any;
  const idbaocao = String(item.idbaocao ?? '');
  const guiDuyet = Boolean(item?.guiDuyet);
  const tinhTrangDuyet = Number(item?.TinhTrangDuyet ?? item?.tinhTrangDuyet ?? -1);
  const nhanVienDuyet = item?.nhanVienDuyet;
  const handleApprove = async (status: 1 | 2) => {
    const executeApprove = async (ghiChu?: string) => {
      try {
        const { tenantID } = useAuthStore.getState();
        const model: RequestDTO = {
          id: record.detail.chiTieuNam.id,
          idBaoCao: idbaocao,
          status,
          phanHe: 'bsc',
          site: tenantID || '',
          ghiChu: ghiChu ?? '',
        };
        const response: any = await reportingApi.approvePheDuyetBaoCao(model);
        if (response?.status !== true) {
          message.error(response?.msg ?? t('bsc.msg.loipheduyetbaocao', 'Có lỗi xảy ra khi phê duyệt báo cáo'));
          return;
        }
        message.success(
          response?.msg ??
            (status === 1 ? t('bsc.msg.pheduyetbaocaothanhcong', 'Phê duyệt báo cáo thành công') : t('bsc.msg.tuchoipheduyetbaocaothanhcong', 'Từ chối phê duyệt báo cáo thành công'))
        );
        try {
          window.dispatchEvent(new CustomEvent('report-kpis:refresh-list'));
          window.dispatchEvent(new CustomEvent('noti-baobaokpi'));
        } catch { }
      } catch (e) {
        console.error('approvePheDuyetBaoCao failed', e);
        message.error(t('bsc.msg.loipheduyetbaocao', 'Có lỗi xảy ra khi phê duyệt báo cáo'));
      }
    };

    const ghiChuRef = { current: '' };
    const content = (
      <div>
        <p style={{ marginBottom: 8 }}>
          {status === 1 ? t('bsc.msg.xacnhanxetduyetbaocao_nay', 'Bạn có chắc xét duyệt báo cáo này?') : t('bsc.msg.xacnhantuchoibaocao_nay', 'Bạn có chắc từ chối báo cáo này?')}
        </p>
        <Input.TextArea
          placeholder={t('bsc.msg.vuilongnhaplydo', 'Vui lòng nhập lý do...')}
          rows={2}
          onChange={(e) => { ghiChuRef.current = e.target.value; }}
        />
      </div>
    );

    const onOk = () => {
      const ghiChu = (ghiChuRef.current ?? '').trim();
      if (!ghiChu) {
        message.error(t('bsc.msg.vuilongnhaplydo', 'Vui lòng nhập lý do...'));
        return Promise.reject();
      }
      return executeApprove(ghiChu);
    };
    if (status === 1) {
      Modal.confirm({
        title: t('bsc.btn.xacnhan', 'Xác nhận'),
        content,
        okText: t('bsc.btn.xacnhan', 'Xác nhận'),
        cancelText: t('bsc.btn.huy', 'Hủy'),
        centered: true,
        onOk,
      });
    } else {
      Modal.confirm({
        title: t('bsc.btn.xacnhan', 'Xác nhận'),
        content,
        okText: t('bsc.btn.xacnhan', 'Xác nhận'),
        cancelText: t('bsc.btn.huy', 'Hủy'),
        centered: true,
        onOk,
      });
    }
  };

  const handleHistory = async () => {
    try {
      const { tenantID } = useAuthStore.getState();
      const model: RequestDTO = {
        id: record.detail.chiTieuNam.id,
        idBaoCao: idbaocao,
        phanHe: 'bsc',
        site: tenantID || '',
      };
      const response = await reportingApi.approveHistories(model);
      if (!response?.data) {
        message.error(t('bsc.msg.khongthetailichsupheduyet', 'Không thể tải lịch sử phê duyệt'));
        return;
      }
      const historyData = response.data as RoleApproveHistoryView;

      Modal.info({
        title: t('bsc.title.lichsupheduyetbaocao', 'Lịch sử phê duyệt báo cáo'),
        width: 900,
        closable: true,
        footer: null,
        content: (
          <ApprovalHistoryTimeline
            historyData={historyData}
            orgLoaders={{ loadEmployee, loadDepartment, loadPosition }}
          />
        ),
      });
    } catch (e) {
      console.error('approveHistories failed', e);
      message.error(t('bsc.msg.loitailichsupheduyet', 'Có lỗi xảy ra khi tải lịch sử phê duyệt'));
    }
  };

  if (guiDuyet) {
    if (tinhTrangDuyet === 0) {
      if (nhanVienDuyet && nhanVienDuyet === userID) {
        return (
          <div
            style={{
              margin: '7px auto 0',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Label color="success" title={t('bsc.text.duyet', 'Duyệt')} onClick={() => handleApprove(1)} />
            <Label color="danger" title={t('bsc.text.khongduyet', 'Không duyệt')} onClick={() => handleApprove(2)} />
            <Label color="info" title={<HistoryOutlined />} onClick={handleHistory} />
          </div>
        );
      } else {
        return (
          <div
            style={{
              textAlign: 'center',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <Label
              color="primary"
              title={
                <span style={{ color: 'white' }}>
                  {t('bsc.label.cap_level', 'Cấp {{level}}', { level: String(item?.capDoDuyet ?? '') })}
                </span>
              }
            />
            <Label color="warning" title={<span style={{ color: '#0b446f' }}>{t('bsc.option.choxetduyet', 'Báo cáo chờ xét duyệt')}</span>} />
            <Label color="info" title={<HistoryOutlined />} onClick={handleHistory} />
          </div>
        );
      }
    }
    if (tinhTrangDuyet === 2) {
      return (
        <div
          style={{
            textAlign: 'center',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <span className="text-danger" style={{ fontWeight: 'bold' }}>
            {t('bsc.msg.solieubaocaokhongduocxetduyet', 'Số liệu báo cáo không được xét duyệt')}
          </span>
          <Label color="info" title={<HistoryOutlined />} onClick={handleHistory} />
        </div>
      );
    }
    if (tinhTrangDuyet === 1) {
      if (item?.dabaocao) {
        return (
          <div
            style={{
              textAlign: 'center',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <span className="text-success" style={{ fontWeight: 'bold' }}>
              {t('bsc.msg.dabaocao', 'Đã báo cáo')}
            </span>
            <Label color="info" title={<HistoryOutlined />} onClick={handleHistory} />
          </div>
        );
      }
      return (
        <span className="text-danger" style={{ fontWeight: 'bold' }}>
          {t('bsc.msg.chuabaocao', 'Chưa báo cáo')}
        </span>
      );
    }
  } else {
    if (item?.dabaocao) {
      return (
        <span className="text-success" style={{ fontWeight: 'bold' }}>
          {t('bsc.msg.dabaocao', 'Đã báo cáo')}
        </span>
      );
    }
    return (
      <span className="text-danger" style={{ fontWeight: 'bold' }}>
        {t('bsc.msg.chuabaocao', 'Chưa báo cáo')}
      </span>
    );
  }

  return null;
};

interface ApprovalHistoryTimelineProps {
  historyData: RoleApproveHistoryView;
  orgLoaders: {
    loadEmployee: ReturnType<typeof useLocalFilter>['loadEmployee'];
    loadDepartment: ReturnType<typeof useLocalFilter>['loadDepartment'];
    loadPosition: ReturnType<typeof useLocalFilter>['loadPosition'];
  };
}

const ApprovalHistoryTimeline: React.FC<ApprovalHistoryTimelineProps> = ({ historyData, orgLoaders }) => {
  const { t } = useTranslation();
  const { loadEmployee, loadDepartment, loadPosition } = orgLoaders;
  const { items = [], baoCao, roleApprove } = historyData;
  const sortedItems = [...items].sort((a, b) => {
    const aAny = a as any;
    const bAny = b as any;
    const aTime = new Date(aAny.ngayTao || aAny.NgayTao || 0).getTime();
    const bTime = new Date(bAny.ngayTao || bAny.NgayTao || 0).getTime();
    if (bTime !== aTime) return bTime - aTime;
    const aType = String(a.type ?? aAny.Type ?? '').toLowerCase();
    const bType = String(b.type ?? bAny.Type ?? '').toLowerCase();
    if (aType === 'choduyet' && bType !== 'choduyet') return -1;
    if (bType === 'choduyet' && aType !== 'choduyet') return 1;
    return 0;
  });

  const getTypeXetDuyetLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      quanlytructiep: t('bsc.module.quanlytructiep', 'Quản lý trực tiếp'),
      captrencaohon: t('bsc.module.captrencaohon', 'Cấp trên cao hơn'),
      nhanvien: t('bsc.label.nhanvien', 'Nhân viên'),
    };
    return typeMap[type] || type;
  };

  const getLabelStyle = (color: 'success' | 'warning' | 'info' | 'primary') => {
    const colorMap = {
      success: { backgroundColor: '#28a745', color: '#fff' },
      warning: { backgroundColor: '#ffc107', color: '#0b446f' },
      info: { backgroundColor: '#17a2b8', color: '#0b446f' },
      primary: { backgroundColor: '#0d6efd', color: '#fff' },
    };
    return {
      padding: '2px 6px',
      borderRadius: '3px',
      fontSize: '12px',
      fontWeight: 600,
      display: 'inline-block',
      marginBottom: '5px',
      ...colorMap[color],
    };
  };

  return (
    <div
      style={{
        display: 'block',
        padding: '5px',
        margin: '0',
        fontSize: '13px',
        lineHeight: '24px',
        color: '#333',
        wordBreak: 'break-word',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ccc',
        borderRadius: '4px',
        width: '100%',
        maxHeight: '500px',
        overflow: 'auto',
      }}
    >
      <ul
        style={{
          listStyleType: 'none',
          position: 'relative',
          padding: '10px 0',
          margin: 0,
        }}
      >
        {/* Vertical timeline */}
        <span
          style={{
            position: 'absolute',
            top: 0,
            left: '29px',
            width: '2px',
            height: '100%',
            backgroundColor: '#d4d9df',
            zIndex: 400,
            display: 'block',
          }}
        />

        {/* Pending approval (fallback cho báo cáo cũ chưa lưu lịch sử chờ duyệt) */}
        {baoCao &&
          baoCao.tinhTrangDuyet === 0 &&
          items.length > 0 &&
          !items.some((item) => {
            const itemAny = item as any;
            const typeApprove = String(item.type ?? itemAny.Type ?? '').toLowerCase();
            const capDo = Number(item.capDo ?? itemAny.CapDo ?? 0);
            const nv = item.nhanVienID || itemAny.NhanVienID;
            return typeApprove === 'choduyet' && capDo === Number(baoCao.capDoDuyet) && nv === baoCao.nhanVienDuyet;
          }) && (
          <li
            style={{
              margin: 0,
              padding: '0 0 15px 50px',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '20px',
                top: '2px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: '3px solid #22c0e8',
                backgroundColor: 'white',
                zIndex: 401,
              }}
            />
            <span style={getLabelStyle('warning')}>Báo cáo chờ xét duyệt</span>
            {baoCao.nhanVienDuyet &&
              (() => {
                const nhanVien = loadEmployee(baoCao.nhanVienDuyet);
                const boPhan = nhanVien?.BPID || nhanVien?.bpid ? loadDepartment(nhanVien.BPID || nhanVien.bpid) : null;
                const chucVu = nhanVien?.CVID || nhanVien?.cvid ? loadPosition(nhanVien.CVID || nhanVien.cvid) : null;

                return (
                  <>
                    <p style={{ margin: '5px 0' }}>
                      <span>{t('bsc.label.nguoixetduyet_colon', 'Người xét duyệt:')} </span>
                      <span style={{ fontWeight: 600 }}>
                        {nhanVien?.TenNhanVien || nhanVien?.tenNhanVien || baoCao.nhanVienDuyet}
                      </span>
                    </p>
                    <p style={{ margin: 0, marginBottom: '5px', display: 'flex', gap: '10px' }}>
                      {chucVu && <span style={{ fontWeight: 600 }}>{chucVu.Ten || chucVu.ten || ''}</span>}
                      {boPhan && <span style={{ fontWeight: 600 }}>{boPhan.Ten || boPhan.ten || ''}</span>}
                    </p>
                  </>
                );
              })()}
          </li>
        )}

        {/* History items */}
        {sortedItems.map((item, index) => {
          const nhanVien = item.nhanVienID ? loadEmployee(item.nhanVienID) : null;
          const nhanVienSoLieu = item.nhanVienSoLieu ? loadEmployee(item.nhanVienSoLieu) : null;
          const boPhan = nhanVien?.BPID || nhanVien?.bpid ? loadDepartment(nhanVien.BPID || nhanVien.bpid) : null;
          const chucVu = nhanVien?.CVID || nhanVien?.cvid ? loadPosition(nhanVien.CVID || nhanVien.cvid) : null;
          const itemAny = item as any;
          const ngayTao = itemAny.ngayTao || itemAny.NgayTao;
          const typeApprove = item.type ?? itemAny.Type;
          const isChoDuyet = typeApprove == 'choduyet';
          const capDo = Number(item.capDo ?? itemAny.CapDo ?? 0);

          return (
            <li
              key={`${item.roleApproveID}-${item.roleApproveDetailID}-${item.itemID || index}`}
              style={{
                margin: 0,
                padding: '0 0 15px 50px',
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: '20px',
                  top: '2px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  border: '3px solid #22c0e8',
                  backgroundColor: 'white',
                  zIndex: 401,
                }}
              />

              {isChoDuyet ? (
                <p style={{ margin: '5px 0', display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={getLabelStyle('warning')}>
                    {t('bsc.label.cap_level', 'Cấp {{level}}', { level: String(capDo) })}: {t('bsc.option.choxetduyet', 'Báo cáo chờ xét duyệt')}
                  </span>
                  {ngayTao && <strong>{CommonJs.formatDateTime(ngayTao)}</strong>}
                </p>
              ) : item.typeXetDuyet ? (
                <span style={getLabelStyle('info')}>
                  <strong>
                    {t('bsc.label.cap_level', 'Cấp {{level}}', { level: String(item.capDo) })}: {getTypeXetDuyetLabel(item.typeXetDuyet)}
                  </strong>
                </span>
              ) : (
                <span style={getLabelStyle('info')}>
                  <strong>Cấp {item.capDo}</strong>
                </span>
              )}

              {!isChoDuyet && (
              <p style={{ margin: '5px 0', display: 'flex', gap: '10px', alignItems: 'center' }}>
                {item.tinhTrangDuyet === 1 && <span style={getLabelStyle('success')}>{t('bsc.text.duyet', 'Duyệt')}</span>}
                {item.tinhTrangDuyet === 2 && <span style={getLabelStyle('warning')}>{t('bsc.text.khongduyet', 'Không duyệt')}</span>}
                {item.tinhTrangDuyet === 0 && item.soLieu && (
                  <span style={getLabelStyle('primary')}>{t('bsc.text.capnhat', 'Cập nhật')}</span>
                )}
                {ngayTao && <strong>{CommonJs.formatDateTime(ngayTao)}</strong>}
              </p>
              )}

              {item.soLieu && (
                <p style={{ margin: 0, marginBottom: '5px' }}>
                  <span>{t('bsc.label.solieu_colon', 'Số liệu:')} </span>
                  <span style={{ fontWeight: 600 }}>{item.soLieu}</span>
                </p>
              )}

              <p style={{ margin: '5px 0' }}>
                <span>{t('bsc.label.nguoixetduyet_colon', 'Người xét duyệt:')} </span>
                {nhanVien?.isDelete || nhanVien?.IsDelete ? (
                  <span style={{ fontWeight: 600, color: 'red', textDecoration: 'line-through' }}>
                    {nhanVien?.TenNhanVien || nhanVien?.tenNhanVien || item.nhanVienID}
                  </span>
                ) : (
                  <span style={{ fontWeight: 600 }}>
                    {nhanVien?.TenNhanVien || nhanVien?.tenNhanVien || item.nhanVienID}
                  </span>
                )}
              </p>

              <p style={{ margin: 0, marginBottom: '5px', display: 'flex', gap: '10px' }}>
                {chucVu && <span style={{ fontWeight: 600 }}>{chucVu.Ten || chucVu.ten || ''}</span>}
                {boPhan && <span style={{ fontWeight: 600 }}>{boPhan.Ten || boPhan.ten || ''}</span>}
              </p>

              {item.ghiChu && (
                <p style={{ margin: 0 }}>
                  <span>{t('bsc.label.ghichu_colon', 'Ghi chú:')} </span>
                  <span style={{ fontWeight: 600 }}>{item.ghiChu}</span>
                </p>
              )}
            </li>
          );
        })}

        {/* Role approve name */}
        {roleApprove && (
          <li
            style={{
              margin: 0,
              padding: '0 0 15px 50px',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '20px',
                top: '2px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: '3px solid #22c0e8',
                backgroundColor: 'white',
                zIndex: 401,
              }}
            />
            <span style={getLabelStyle('primary')}>{roleApprove.name || ''}</span>
          </li>
        )}
      </ul>
    </div>
  );
};
